import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { validateLoginForm, hasFormErrors } from '../../utils/validation';
import { Eye, EyeOff } from 'lucide-react';
import type { LoginFormData, FormErrors } from '../../types/auth';

interface LoginFormProps {
  onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const { signIn, loading, error, clearError } = useAuth();
  const [formData, setFormData] = useState<LoginFormData>({ email: '', password: '' });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (formErrors[name as keyof FormErrors]) setFormErrors(p => ({ ...p, [name]: undefined }));
    if (error) clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateLoginForm(formData);
    setFormErrors(errors);
    if (hasFormErrors(errors)) return;
    const { error: signInError } = await signIn(formData.email, formData.password);
    if (!signInError && onSuccess) onSuccess();
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em', marginBottom: '0.375rem' }}>
          Sign in
        </h2>
        <p style={{ fontSize: '0.875rem', color: '#475569' }}>
          Welcome back to Nitrextrader
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} aria-label="Sign in form">
        <Input
          label="Email Address"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          error={formErrors.email}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />

        <div style={{ position: 'relative' }}>
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={formErrors.password}
            placeholder="Enter your password"
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: 10, top: 30,
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#334155', padding: 2,
              display: 'flex', alignItems: 'center',
              transition: 'color 0.18s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#94a3b8'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#334155'; }}
          >
            {showPassword ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
          </button>
        </div>

        {error && (
          <div
            role="alert"
            aria-live="polite"
            style={{
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 8,
              padding: '0.75rem 0.875rem',
              fontSize: '0.8125rem',
              color: '#f87171',
            }}
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={loading}
          className="w-full"
          ariaLabel="Sign in to your account"
          style={{ width: '100%', marginTop: '0.25rem', borderRadius: 9 }}
        >
          {loading ? 'Signing in…' : 'Sign In'}
        </Button>
      </form>

      <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
        <Link
          to="/auth/forgot-password"
          style={{ fontSize: '0.8125rem', color: '#3b82f6', fontWeight: 500, transition: 'color 0.18s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#60a5fa'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#3b82f6'; }}
        >
          Forgot your password?
        </Link>
        <p style={{ fontSize: '0.8125rem', color: '#334155' }}>
          No account?{' '}
          <Link
            to="/auth/signup"
            style={{ color: '#3b82f6', fontWeight: 600, transition: 'color 0.18s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#60a5fa'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#3b82f6'; }}
          >
            Sign up free
          </Link>
        </p>
      </div>
    </div>
  );
};
